
import React, {useState, useRef, useEffect} from "react";
import {ToastContainer, toast} from "react-toastify";
import Translate from "../../../Components/Multilanguage/Translate";
import GiphySearchbox from 'react-giphy-searchbox';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import Gif from '@mui/icons-material/Gif';
import CloseIcon from '@mui/icons-material/Close';
import {commentGifUploadThumbnail, commentImageUploadThumbnail, createComment, editComment} from "../service";
import {useTranslateText} from "../../../Components/Multilanguage/Translate";
import {useSelector} from "react-redux";
import styles from './scss/_postcomment.module.scss';
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import {createTurndown, formatMarkdownToHtml} from "../utils/helper";


const Link = Quill.import("formats/link");

class CustomLink extends Link {
    static create(value) {
        let node = super.create(value);
        node.setAttribute("class", "customlink");
        node.setAttribute("target", "_blank");
        return node;
    }
}

Quill.register("formats/link", CustomLink);

const quillModules = {
    toolbar: [
        ["bold", "italic", "underline","link","clean"]
    ],
};

const quillFormats = ["bold", "italic", "underline", "link"];

const turndown = createTurndown();

const PostComment = ({ postId,id= null, projectInfo, parentCommentId = null, onCommentPosted, initialContent="", isEditComment=false, hideMedia = false}) => {
    const userDetails = useSelector((state) => state?.userDetails?.value);
    const accessToken = useSelector((state) => state?.accessToken?.value);
    const [commentInputValue, setCommentInputValue] = useState("");
    const [selectedGif, setSelectedGif] = useState(null);
    const [imageDimensions, setImageDimensions] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [isGiphyModalOpen, setIsGiphyModalOpen] = useState(false);
    const appInfo = {projectDetails: projectInfo, accessToken};
    const gifApiKey = projectInfo?.projectConfig?.config?.GIPHY_API_KEY;
    const theme = projectInfo?.projectConfig?.config?.THEME;
    const fileInputRef = useRef(null);
    const translateText = useTranslateText();
    const fileInputId = parentCommentId ? `file-reply-${parentCommentId}` : `file-main-${postId}`;

    useEffect(() => {
        const formattedContent = formatMarkdownToHtml(initialContent);
        setCommentInputValue(formattedContent)
    }, [initialContent]);

    /**
     * On GIF selection from Giphy modal, Reset imageSelection and preview states
     * only one media type can be selected at a time
     * @param item
     * @returns {Promise<void>}
     */
    const onGifSelect = async (item) => {
        const gifUrl = item?.images?.fixed_width_small?.url;
        const response = await commentGifUploadThumbnail(gifUrl, appInfo);
        if (response.data.data) {
            // setImageDimensions(null);
            setSelectedGif(item);
            // setImagePreview(null);
            setIsGiphyModalOpen(false);
        }
    };

    /**
     * On image file selection, validate and upload the image
     * Reset GIF selection state
     * @param e
     * @returns {Promise<void>}
     */
    const handleFileChange = async (e) => {
        var allowedExtensions = /(\.jpg|\.jpeg|\.png|\.gif)$/i;
        if (!allowedExtensions.exec(e?.target?.files[0]?.name)) {
            alert(<Translate textKey={'invalid_extension_message'}/>);
        } else {
            setSelectedGif(null);
            const files = e?.target?.files;
            const response = await commentImageUploadThumbnail(files[0], appInfo);
            if (response.data.data) {
                const reader = new FileReader();
                reader.onloadend = () => {
                    const img = new window.Image();
                    img.onload = () => {
                        setImageDimensions({
                            width: parseInt(img.width), height: parseInt(img.height), url: response.data.data
                        });
                    };
                    img.src = reader.result;
                    setImagePreview(reader.result);
                };
                reader.readAsDataURL(files[0]);
                if (fileInputRef.current) {
                    fileInputRef.current.value = null;
                }
            }
        }
    };

    const handleRemoveSelection = () => {
        setSelectedGif(null);
        setImagePreview(null);
    };

    const postCommentHandler = async (post_id) => {
        if (commentInputValue || selectedGif || imageDimensions) {
             if (!validateAllLinks(commentInputValue)) {
            toast.error("Invalid link detected. Please enter a valid URL.", {
                position: "top-center",
            });
            return;
        }

            const markdownContent = turndown.turndown(commentInputValue);
            // Validate all markdown links before posting
            const linkRegex = /\[([^\]]+)\]\(([^\)]+)\)/g;
            let match;
            let hasInvalidLink = false;
            while ((match = linkRegex.exec(markdownContent)) !== null) {
                const url = match[2];
                if (!isValidUrl(url)) {
                    hasInvalidLink = true;
                    break;
                }
            }
            if (hasInvalidLink) {
                toast.error('Please enter only valid hyperlinks in your comment.', {position: 'top-center'});
                return;
            }
            let comment_data = {
                content: markdownContent || '',
                post_id: Number(post_id),
            };
            if (selectedGif){
                comment_data['gif[height]'] = selectedGif.images.fixed_width_small.height;
                comment_data['gif[width]'] = selectedGif.images.fixed_width_small.width;
                comment_data['gif[url]'] = selectedGif.images.fixed_width_small.url;
            }
            if (imageDimensions) {
                comment_data['image[height]'] = imageDimensions.height;
                comment_data['image[width]'] = imageDimensions.width;
                comment_data['image[url]'] = imageDimensions.url;
            }
            if (parentCommentId) {
                comment_data.comment_id = parentCommentId;
            }
            try {
                const res = isEditComment
                    ? await editComment(appInfo, id ?? parentCommentId, comment_data)
                    : await createComment(appInfo, comment_data);
                resetLocalState();
                if (onCommentPosted) onCommentPosted(res,commentInputValue );
            } catch (err) {
                console.log(err)
                toast.error(<Translate textKey={'comment_or_image_required'}/>, {position: 'top-center'});
            }
        } else {
            toast.error(<Translate textKey={'comment_or_image_required'}/>, {position: 'top-center'});
        }
    };

    /**
     * Reset local states after comment posting
     */
    function resetLocalState(){
        setCommentInputValue('');
        setSelectedGif(null);
        setImagePreview(null);
        setImageDimensions(null)
    }

    // --- Quill Link Tooltip Validation Patch ---
function isValidUrl(url) {
    const pattern = new RegExp(
        '^(https?:\\/\\/)' +                // require http or https
        '([\\w-]+\\.)+[\\w-]{2,}' +         // domain (example.com)
        '(\\/[^\\s]*)?$',                   // optional path
        'i'
    );

    return pattern.test(url);
}

function validateAllLinks(html) {
    const temp = document.createElement("div");
    temp.innerHTML = html;

    const links = temp.querySelectorAll("a");

    for (let link of links) {
        let href = link.getAttribute("href");

        href = normalizeUrl(href);

        if (!isValidUrl(href)) {
            return false;
        }
    }

    return true;
}
if (typeof window !== "undefined" && window.Quill) {
    const Quill = window.Quill;
    const LinkTooltip = Quill.import('ui/tooltip');
    if (LinkTooltip && !LinkTooltip.prototype._patchedForValidation) {
        const originalSave = LinkTooltip.prototype.save;
        // LinkTooltip.prototype.save = function () {
        //     const value = this.textbox.value;
        //     if (this.root.classList.contains('ql-link-tooltip')) {
        //         if (!isValidUrl(value)) {
        //             this.textbox.classList.add('ql-tooltip-error');
        //             this.textbox.setCustomValidity('Please enter a valid URL');
        //             this.textbox.reportValidity();
        //             return;
        //         } else {
        //             this.textbox.classList.remove('ql-tooltip-error');
        //             this.textbox.setCustomValidity('');
        //         }
        //     }
        //     originalSave.call(this);
        // };

        LinkTooltip.prototype.save = function () {
    const value = this.textbox.value.trim();

    if (this.root.classList.contains('ql-link-tooltip')) {
        if (!isValidUrl(value)) {
            this.textbox.classList.add('ql-tooltip-error');
            this.textbox.setCustomValidity('Please enter a valid URL');
            this.textbox.reportValidity();
            return;
        } else {
            this.textbox.classList.remove('ql-tooltip-error');
            this.textbox.setCustomValidity('');
        }
    }

    originalSave.call(this);
};
        LinkTooltip.prototype._patchedForValidation = true;
    }
}

function normalizeUrl(url) {
    if (!url) return '';
    return url.startsWith('http') ? url : `https://${url}`;
}

    return (
        <div className={styles.textContainer}>
            <div className={styles.imageContainer}>
                {
                    userDetails?.user_image ? (
                        <img src={userDetails?.user_image} alt="Profile"/>
                    ) : (<span className="nameAvatar">{userDetails?.first_name?.slice(0, 1)?.toUpperCase()}</span>)
                }
            </div>
            <ToastContainer/>

            <div className={styles.inputContainer}>
                <ReactQuill
                    bounds={'.quillEditor'}
                    theme="snow"
                    value={commentInputValue}
                    onChange={setCommentInputValue}
                    modules={quillModules}
                    formats={quillFormats}
                    placeholder={translateText("add_your_comment")}
                    className={styles.quillEditor}
                />
                <div className={styles.flexcontainer}>
                    {(selectedGif || imagePreview) && <div className={styles.selectedImage}
                          data-postcomment-instance={parentCommentId ? `reply-${parentCommentId}` : `main-${postId}`}>
                        {selectedGif && (
                            <>
                                <button className={styles.removeBtn} onClick={handleRemoveSelection}>
                                    <CloseIcon/>
                                </button>
                                <img src={selectedGif.images.fixed_height.url} className={styles.selectedRawimage}
                                     alt="Selected Gif"/>
                            </>
                        )}
                        {imagePreview && (
                            <>
                                <button className={styles.removeBtn} onClick={handleRemoveSelection}>
                                    <CloseIcon/>
                                </button>
                                <img src={imagePreview} className={styles.selectedRawimage} alt="Selected Image"/>
                            </>
                        )}
                    </div>}

                    <div className={styles.actions}>
                        <div className={styles.buttonContainer} style={{display: hideMedia ? 'none' : 'flex'}}>
                            <label htmlFor={fileInputId}
                                   className={`${styles.customFileUpload} ${theme === 'LIGHT' ? 'light-theme' : ''}`}>
                                <PhotoCamera/>
                            </label>
                            <input style={{display: "none"}} type="file" id={fileInputId} accept="image/*"
                                   onChange={handleFileChange}
                                   ref={fileInputRef}/>

                            <button className={`${styles.giphyButton} ${theme === 'LIGHT' ? 'light-theme' : ''}`}
                                    onClick={() => setIsGiphyModalOpen(true)}>
                                <Gif/>
                            </button>
                        </div>
                        <button className={styles.submit} onClick={()=>{postCommentHandler(postId)}}>
                            <Translate textKey="post"/>
                        </button>
                    </div>
                </div>
                {isGiphyModalOpen && (
                    <div className="customModal">
                        <div className="modalContent">
                            <button className="closeButton" onClick={() => setIsGiphyModalOpen(false)}>
                                &times;
                            </button>
                            <GiphySearchbox
                                apiKey={gifApiKey}
                                onSelect={onGifSelect}
                                searchPlaceholder={translateText("search")}
                                masonryConfig={[
                                    {columns: 2, imageWidth: 110, gutter: 5},
                                    {mq: '700px', columns: 3, imageWidth: 120, gutter: 5}
                                ]}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default PostComment;